import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import ProtectedRoute from '../../components/ProtectedRoute';
import AdminLayout, { useTheme } from '../../components/AdminLayout';
import { useNotification } from '../../contexts/NotificationContext';

const ROLES = ['user', 'shop_manager', 'branch_manager', 'super_admin', 'worker'];

const TABS = [
  { id: 'all', labelKey: 'admin.allUsers', fallback: 'All', role: null },
  { id: 'admins', labelKey: 'admin.admins', fallback: 'Admins', role: 'super_admin' },
  { id: 'salon_managers', labelKey: 'admin.salonManagers', fallback: 'Salon Managers', role: 'shop_manager' },
  { id: 'branch_managers', labelKey: 'admin.branchManagers', fallback: 'Branch Managers', role: 'branch_manager' },
  { id: 'users', labelKey: 'admin.regularUsers', fallback: 'Users', role: 'user' },
];

function UsersContent() {
  const { t } = useTranslation();
  const { showAlert, showConfirm } = useNotification();
  const { T, S } = useTheme();
  const [users, setUsers] = useState([]);
  const [shops, setShops] = useState([]);
  const [allBranches, setAllBranches] = useState([]);
  const [roleCounts, setRoleCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [activeTab, setActiveTab] = useState('all');

  // New state: selected salon filter inside the modal (for branch managers)
  const [modalShopId, setModalShopId] = useState('');

  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'user', shop_id: '', branch_id: '' });

  // Filter branches for the dropdown if a shop is selected
  const filteredBranches = allBranches.filter(b => !modalShopId || b.shop_id === parseInt(modalShopId));

  const getRoleForTab = () => TABS.find(tab => tab.id === activeTab)?.role ?? null;

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const role = getRoleForTab();
      const [userData, shopData, branchData, dashboardData] = await Promise.all([
        api.getAdminUsers(role),
        api.getAdminShops(),
        api.getBranches(),
        api.getAdminDashboard(),
      ]);
      setUsers(userData);
      setShops(shopData);
      setAllBranches(branchData);
      setRoleCounts({
        ...(dashboardData?.users_by_role || {}),
        total: dashboardData?.total_users ?? null,
      });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, [activeTab]);

  const getTabCount = (tab) => {
    if (!roleCounts) return null;
    if (tab.id === 'all') return roleCounts.total ?? null;
    const key = tab.id === 'admins' ? 'admins' : tab.id === 'salon_managers' ? 'salon_managers' : tab.id === 'branch_managers' ? 'branch_managers' : 'users';
    return roleCounts[key] ?? null;
  };

  const branchById = (id) => allBranches.find(b => b.id === id);
  const shopById = (id) => shops.find(s => s.id === id);

  // Derive Salon/Branch names for table
  const getSalonName = (u) => {
    if (u.shop_id) return shopById(u.shop_id)?.name;
    if (u.branch_id) {
      const b = branchById(u.branch_id);
      return shopById(b?.shop_id)?.name;
    }
    return '—';
  };
  const getBranchName = (u) => {
    if (u.branch_id) return branchById(u.branch_id)?.name;
    return '—';
  };

  const openCreate = () => {
    setEditing('create');
    setModalShopId('');
    setForm({ email: '', password: '', name: '', role: 'user', shop_id: '', branch_id: '' });
  };

  const openEdit = (u) => {
    setEditing(u.id);
    // If editing a branch manager, pre-fill the shop selector based on their branch
    let derivedShopId = '';
    if (u.role === 'branch_manager' && u.branch_id) {
      const b = branchById(u.branch_id);
      if (b) derivedShopId = String(b.shop_id);
    } else if (u.role === 'shop_manager' && u.shop_id) {
      derivedShopId = String(u.shop_id);
    }

    setModalShopId(derivedShopId);
    setForm({
      email: u.email,
      password: '',
      name: u.name,
      role: u.role,
      shop_id: u.shop_id ?? '',
      branch_id: u.branch_id ?? ''
    });
  };

  const handleShopChange = (sId) => {
    setModalShopId(sId);
    // If usage context is shop_manager, also set form.shop_id
    if (form.role === 'shop_manager') {
      setForm(f => ({ ...f, shop_id: sId, branch_id: '' }));
    } else if (form.role === 'branch_manager') {
      // Just filter branches, clear selected branch if mismatch
      setForm(f => ({ ...f, branch_id: '' }));
    }
  };

  // When role changes, reset relevant IDs
  const handleRoleChange = (newRole) => {
    setForm(f => ({ ...f, role: newRole, shop_id: '', branch_id: '' }));
    setModalShopId('');
  };

  const save = async () => {
    try {
      const shopId = form.role === 'shop_manager' && form.shop_id ? parseInt(form.shop_id) : null;
      const branchId = form.role === 'branch_manager' && form.branch_id ? parseInt(form.branch_id) : null;

      const payload = {
        email: form.email,
        name: form.name,
        role: form.role,
        shop_id: shopId,
        branch_id: branchId
      };
      if (form.password) payload.password = form.password;

      if (editing === 'create') {
        if (!payload.password) return showAlert(t('admin.passwordRequired'));
        await api.createAdminUser(payload);
      } else {
        await api.updateAdminUser(editing, payload);
      }
      setEditing(null); fetchUsers();
    } catch (err) { showAlert(err.message); }
  };

  const remove = async (id) => {
    showConfirm(t('admin.deleteUserConfirm'), async () => {
      try { await api.deleteAdminUser(id); fetchUsers(); }
      catch (err) { showAlert(err.message); }
    });
  };

  const labelStyle = { display: 'block', fontSize: '0.75rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' };

  return (
    <AdminLayout role="admin">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '2rem' }}>
        <div>
        <h1 style={S.pageTitle}>{t('admin.users')}</h1>
        <div style={{ width: '40px', height: '2px', background: T.orange }} />
        </div>
        <button style={S.btnOrange} onClick={openCreate}
          onMouseEnter={e => e.target.style.background = T.orangeHov}
          onMouseLeave={e => e.target.style.background = T.orange}>
          + {t('admin.addUser')}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 0, marginBottom: '1.5rem', borderBottom: `1px solid ${T.border}`, flexWrap: 'wrap' }}>
        {TABS.map(tab => {
          const count = getTabCount(tab);
          const label = t(tab.labelKey) || tab.fallback;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                background: 'none',
                border: 'none',
                color: activeTab === tab.id ? T.orange : T.muted,
                padding: '0.5rem 1rem',
                cursor: 'pointer',
                fontWeight: activeTab === tab.id ? 600 : 500,
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '0.9rem',
                transition: 'color 0.2s',
                borderBottom: activeTab === tab.id ? `2px solid ${T.orange}` : '2px solid transparent',
              }}
            >
              {label}
              {count != null && <span style={{ marginLeft: '0.35rem', opacity: 0.9 }}>({count})</span>}
            </button>
          );
        })}
      </div>

      {editing && (
        <div style={S.modal}>
          <div style={S.modalBox}>
            <h2 style={S.modalTitle}>{editing === 'create' ? t('admin.newUser') : t('admin.editUser')}</h2>

            <input placeholder={t('admin.email')} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} style={S.input} />
            <input type="password" placeholder={t('admin.passwordLeaveBlank')} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} style={S.input} />
            <input placeholder={t('admin.name')} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={S.input} />

            <label style={labelStyle}>{t('admin.role')}</label>
            <select value={form.role} onChange={e => handleRoleChange(e.target.value)} style={S.select}>
              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>

            {/* If Shop Manager: Assign Salon directly */}
            {form.role === 'shop_manager' && (
              <>
                <label style={labelStyle}>Assign Salon</label>
                <select value={form.shop_id} onChange={e => setForm({ ...form, shop_id: e.target.value })} style={S.select}>
                  <option value="">{t('admin.selectSalon')}</option>
                  {shops.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </>
            )}

            {/* If Branch Manager: Filter Salon -> Assign Branch */}
            {form.role === 'branch_manager' && (
              <>
                <label style={labelStyle}>1. Filter by Salon</label>
                <select value={modalShopId} onChange={e => handleShopChange(e.target.value)} style={S.select}>
                  <option value="">{t('admin.selectSalon')}</option>
                  {shops.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>

                <label style={labelStyle}>2. Assign Branch</label>
                <select value={form.branch_id} onChange={e => setForm({ ...form, branch_id: e.target.value })} style={S.select} disabled={!modalShopId}>
                  <option value="">— {modalShopId ? t('admin.selectBranchPlaceholder') : t('admin.selectSalonFirstPlaceholder')} —</option>
                  {filteredBranches.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
              <button onClick={save} style={{ ...S.btnOrange, flex: 1 }}>{t('common.save')}</button>
              <button onClick={() => setEditing(null)} style={S.btnOutline}>{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {loading ? <p style={{ color: T.muted }}>{t('common.loading')}</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>
                {['ID', 'Email', t('admin.phone'), 'Name', 'Role', 'Salon', 'Branch', t('admin.verificationMethod'), 'Actions'].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{ transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(27,213,173,0.04)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={S.td}>{u.id}</td>
                  <td style={S.td}>{u.email ?? '—'}</td>
                  <td style={S.td}>{u.phone_number ?? '—'}</td>
                  <td style={S.td}>{u.name}</td>
                  <td style={S.td}>
                    <span style={{
                      color: u.role === 'super_admin' ? '#ef4444' : u.role.includes('manager') ? T.orange : T.white,
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: '0.8rem',
                      letterSpacing: '0.08em'
                    }}>
                      {u.role}
                    </span>
                  </td>
                  <td style={{ ...S.td, color: T.orange, fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', letterSpacing: '0.05em' }}>
                    {getSalonName(u)}
                  </td>
                  <td style={S.td}>{getBranchName(u)}</td>
                  <td style={S.td}>{u.auth_method ?? '—'}</td>
                  <td style={S.td}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button style={S.btnSmall} onClick={() => openEdit(u)}
                        onMouseEnter={e => e.target.style.color = T.white}
                        onMouseLeave={e => e.target.style.color = T.muted}>Edit</button>
                      <button style={S.btnDanger} onClick={() => remove(u.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

export default function Users() {
  return <ProtectedRoute requireSuperAdmin><UsersContent /></ProtectedRoute>;
}
